import { Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FilterTitleComponent } from '../../common/filter-title/filter-title.component';
import { Filter } from '../../models/filter-models';
import { FilterService } from '../../services/filter.service';
import { AutocompleteComponent } from '../../common/autocomplete/autocomplete.component';
import { ItemDataService } from '../../services/item-data.service';
import { BaseComponent } from '../../common/base.component';
import { socketGroupKeyDown } from '../../common/helper';
import { ActiveRules } from '../../services/filter-generator.service';
import { EquipmentTierPickerComponent } from '../../common/equipment-tier-picker/equipment-tier-picker.component';
import { Rarity } from '../../models/enums';
import { EquipmentType } from "../../models/equipment-type.enum";
import { DynamicContentComponent } from '../../common/dynamic-content/dynamic-content.component';
import { levelingGoldThresholds, levelingVerisiumThresholds } from '../../services/generators/general-generator.service';

@Component({
  selector: 'app-leveling-filters',
  imports: [FormsModule, FilterTitleComponent, AutocompleteComponent, EquipmentTierPickerComponent, DynamicContentComponent],
  templateUrl: './leveling-filters.component.html',
  styleUrl: './leveling-filters.component.scss'
})
export class LevelingFiltersComponent extends BaseComponent {
  @Input({ required: true }) filter!: Filter;
  @Input({ required: true }) activeRules!: ActiveRules;

  weaponClasses: { name: string, image: string }[] = [];
  defenceTypes: { name: string, image: string }[] = [];

  showLevelingGearFilters = false;

  socketGroupKeyDown = socketGroupKeyDown;

  EquipmentType = EquipmentType;
  Rarity = Rarity;

  levelingGoldTooltip: SafeHtml;
  levelingVerisiumTooltip: SafeHtml;

  constructor(
    private filterService: FilterService,
    private itemDataService: ItemDataService,
    private sanitizer: DomSanitizer
  ) {
    super();
    this.weaponClasses = this.itemDataService.getAllWeaponTypes().map(w => ({ name: w, image: this.itemDataService.getItemData().find(i => i.itemClass === w)!.baseTypes[0].image! }));
    this.defenceTypes = this.itemDataService.getAllDefenceTypes();
    this.levelingGoldTooltip = this.sanitizer.bypassSecurityTrustHtml(this.formatLevelingGoldThresholdsForTooltip());
    this.levelingVerisiumTooltip = this.sanitizer.bypassSecurityTrustHtml(this.formatLevelingVerisiumThresholdsForTooltip());
  }

  formatLevelingGoldThresholdsForTooltip(): string {
    const thresholds = levelingGoldThresholds;
    const lines = [
      'The following gold stacks will be hidden & highlighted automatically by the Area Level:<br />',
      ...thresholds.map((t, i) => {
        const fromLevel = i === 0 ? 1 : thresholds[i - 1].maxAreaLevel! + 1;
        return `- From Area Level ${fromLevel}: hide &lt; ${t.hideBelow} &amp; highlight &gt; ${t.highlightAbove}`;
      }),
    ];
    return lines.join('<br />') + '<br /><br />When this is enabled, you cannot use the \'Hide Gold\' filter.';
  }

  formatLevelingVerisiumThresholdsForTooltip(): string {
    const thresholds = levelingVerisiumThresholds;
    const lines = [
      'The following Verisium stacks will be hidden & highlighted automatically by the Area Level:<br />',
      ...thresholds.map((t, i) => {
        const fromLevel = i === 0 ? 1 : thresholds[i - 1].maxAreaLevel! + 1;
        return `- From Area Level ${fromLevel}: hide &lt; ${t.hideBelow} &amp; highlight &gt; ${t.highlightAbove}`;
      }),
    ];
    return lines.join('<br />') + '<br /><br />When this is enabled, you cannot use the \'Hide Verisium\' filter.';
  }

  toggleShowLevelingGear = () => {
    this.filter.showLevelingGear = !this.filter.showLevelingGear;
    this.filter.showLevelingGearWeapons =  this.filter.showLevelingGear;
    this.filter.showLevelingGearArmour =  this.filter.showLevelingGear;

    if (!this.filter.showLevelingGear) {
      this.showLevelingGearFilters = false;
      // When manually turning off leveling gear, we want to turn on 'my weapons/armour' rules instead
      this.filter.weaponFilters.forEach(w => w.show = true);
      this.filter.armourFilters.forEach(a => a.show = true);
      this.filter.groupVisibility.showMyWeaponFilters = true;
      this.filter.groupVisibility.showMyArmourFilters = true;
    }

    this.updateFilter();
  }

  toggleShowLevelingGearWeapons = () => {
    this.filter.showLevelingGearWeapons = !this.filter.showLevelingGearWeapons;
    if (!this.filter.showLevelingGearWeapons && !this.filter.showLevelingGearArmour) {
      this.filter.showLevelingGear = false;
      this.showLevelingGearFilters = false;
    } else if (!this.filter.showLevelingGear) {
      this.filter.showLevelingGear = true;
    }
    this.updateFilter();
  }

  levelingGearWeaponClassesChanged(newValues: string[]) {
    this.filter.showLevelingGearWeaponClasses = newValues;
    this.updateFilter();
  }

  toggleShowLevelingGearArmour = () => {
    this.filter.showLevelingGearArmour = !this.filter.showLevelingGearArmour;
    if (!this.filter.showLevelingGearArmour && !this.filter.showLevelingGearWeapons) {
      this.filter.showLevelingGear = false;
      this.showLevelingGearFilters = false;
    } else if (!this.filter.showLevelingGear) {
      this.filter.showLevelingGear = true;
    }
    this.updateFilter();
  }

  levelingGearArmourDefenceTypesChanged(newValues: string[]) {
    this.filter.showLevelingGearArmourDefenceTypes = newValues;
    this.updateFilter();
  }

  toggleShowLevelingGearExcellentSocketed = () => {
    this.filter.showLevelingGearExcellentSocketed = !this.filter.showLevelingGearExcellentSocketed;
    if (!this.filter.showLevelingGearExcellentSocketed) {
      this.filter.showLevelingGearOtherSocketed = false;
    }
    this.updateFilter();
  }

  toggleShowLevelingGearOtherSocketed = () => {
    this.filter.showLevelingGearOtherSocketed = !this.filter.showLevelingGearOtherSocketed;
    this.updateFilter();
  }

  toggleShowLevelingGearRare = () => { this.filter.showLevelingGearRare = !this.filter.showLevelingGearRare; this.updateFilter(); }
  toggleShowLevelingGearMagic = () => { this.filter.showLevelingGearMagic = !this.filter.showLevelingGearMagic; this.updateFilter(); }
  toggleShowLevelingGearNormal = () => { this.filter.showLevelingGearNormal = !this.filter.showLevelingGearNormal; this.updateFilter(); }

  toggleShowLevelingFlasks = () => {
    this.filter.showLevelingFlasks = !this.filter.showLevelingFlasks;
    if (this.filter.showLevelingFlasks) {
      this.filter.hideLifeFlasks = false;
      this.filter.hideManaFlasks = false;
      this.filter.hideHybridFlasks = false;
      this.filter.hideUtilityFlasks = false;
      this.filter.hideTinctures = false;
      this.filter.hideCharms = false;
    }
    this.updateFilter();
  }
  toggleShowLevelingFlasksAlwaysHideHybrid = () => { this.filter.showLevelingFlasksAlwaysHideHybrid = !this.filter.showLevelingFlasksAlwaysHideHybrid; this.updateFilter(); }

  toggleShowLevelingGold = () => {
    this.filter.showLevelingGold = !this.filter.showLevelingGold;
    if (this.filter.showLevelingGold) {
      this.filter.hideGold = false;
    }
    this.updateFilter();
  }

  toggleShowLevelingVerisium = () => {
    this.filter.showLevelingVerisium = !this.filter.showLevelingVerisium;
    if (this.filter.showLevelingVerisium) {
      this.filter.hideVerisium = false;
    }
    this.updateFilter();
  }

  toggleShowLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes = () => { this.filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes = !this.filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes; this.updateFilter(); }

  toggleShowLevelingGearAllMagicRareBoots = () => { this.filter.showLevelingGearAllMagicRareBoots = !this.filter.showLevelingGearAllMagicRareBoots; this.updateFilter(); }

  updateFilter = () => { this.filterService.updateFilter(); }
}

